import { Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { CashFlow, CashFlowType } from '@api/CashFlows/CashFlow';
import CashFlowsService from '@api/CashFlows/CashFlowCtrl';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { currencyFormatter } from '@utils/Numbers';
import { cashFlowsOfType } from '@utils/CashFlows';
interface ModalCashFlowProps {
    modalCashFlow: OpenModalHookType;
    cashFlowType: CashFlowType;
    startDate: Date;
    endDate: Date;
    /** 'all' = todas las sucursales; sin él usa la sucursal en operación */
    branchOfficeId?: string;
}
export const ModalCashFlow: React.FunctionComponent<ModalCashFlowProps> = (props: any) => {
    const { modalCashFlow, cashFlowType, startDate, endDate, branchOfficeId } = props;
    const cashFlowsService = useMemo(() => new CashFlowsService(), []);
    const [cashFlow, setCashFlow] = useState<CashFlow[]>([]);
    const [selectedCashFlow, setSelectedCashFlow] = useState<CashFlow | undefined>(undefined);
    const modalConfirmation = useConfirmationModal();
    const handleClose = () => {
        modalCashFlow.toggle();
    };

    const onDeleteCashFlow = async () => {
        try {
            if (selectedCashFlow?.id) {
                await cashFlowsService.deleteCashFlow(selectedCashFlow.id);
                void getData();
            }
        } catch (error) {
            showAlertError(error);
        }
    };
    const onConfirmDeleteCashFlow = (cashFlow: CashFlow) => {
        setSelectedCashFlow(cashFlow);
        modalConfirmation.setModalData(
            'Eliminar flujo de caja',
            `Estas seguro de que deseas eliminar el flujo de caja?`
        );
    };
    const getData = async () => {
        showLoader();
        try {
            const cashFlowData = await cashFlowsService.getCashFlows(startDate, endDate, branchOfficeId);
            const cashFlowFilter = cashFlowsOfType(cashFlowData, cashFlowType);
            setCashFlow(cashFlowFilter);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        if (modalCashFlow.modalOpen) {
            void getData();
        }
    }, [modalCashFlow.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 25, padding: 4 }
            }}
            open={modalCashFlow.modalOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {cashFlowType === CashFlowType.Input && (
                        <Typography variant="h5" fontWeight={600}>
                            Otros ingresos
                        </Typography>
                    )}
                    {cashFlowType === CashFlowType.Output && (
                        <Typography variant="h5" fontWeight={600}>
                            Otros gastos
                        </Typography>
                    )}
                    {cashFlowType === CashFlowType.Shipping && (
                        <Typography variant="h5" fontWeight={600}>
                            Gastos de envio
                        </Typography>
                    )}
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider />
                    {cashFlow.map((item, index) => (
                        <Stack key={index} direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={2} alignItems="center">
                                {/* Solo los ingresos se borran; un gasto se cancela en la pestaña Gastos. */}
                                {cashFlowType === CashFlowType.Input && (
                                    <IconButton onClick={() => onConfirmDeleteCashFlow(item)}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                )}
                                <Typography variant="body1">{item.description}</Typography>
                            </Stack>
                            <Typography variant="body1">{currencyFormatter(item.amount)}</Typography>
                        </Stack>
                    ))}
                    {cashFlow.length === 0 && <Typography variant="body1">No hay datos</Typography>}
                </Stack>
            </DialogContent>
            <ModalConfirmation
                needAuthorization
                modalConfirmation={modalConfirmation}
                onConfirmation={onDeleteCashFlow}
            />
        </Dialog>
    );
};
